<template>
  <PageLayout
    :page-title="pageTitle"
    :show-breadcrumbs="false"
    :show-page-header="false"
    :locale="locale"
  >
    <div class="col-xl-8 offset-xl-2">
      <!-- Feature Opt-in Banner -->
      <FeatureOptInBanner
        v-if="showBanner"
        feature-name="useAppointmentV2"
        opt-in-title="Try our new appointment interface!"
        opt-in-description="Experience faster booking with visual slot selection, improved patient search, and a cleaner interface."
        opt-in-button-text="Try it now"
        opt-in-icon="fas fa-sparkles"
        feedback-title="You're using the new interface!"
        feedback-description="Want to go back to the classic version?"
        revert-button-text="Use classic version"
        feedback-icon="fas fa-lightbulb"
        enabled-message="Welcome to the new interface! You can switch back anytime."
        disabled-message="Switched to classic version. You can try the new interface again anytime!"
      />

      <div class="card" ref="formContainer">
        <div class="card-header" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 1rem 1.5rem;">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <i class="fas fa-arrow-left mr-3" style="cursor: pointer; font-size: 1.2rem;" @click="goBack"></i>
              <h4 class="mb-0">{{ $t('appointments.newAppointment') || 'New Appointment' }}</h4>
            </div>
          </div>
        </div>
        <div class="card-body" id="axiosForm">
          <!-- Loading Skeleton -->
          <AppointmentFormSkeleton v-if="isInitialLoading" />

          <!-- Actual Form Content -->
          <div v-else>
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Business <span class="text-danger">*</span></label>
              <multiselect
                v-model="selectedBusiness"
                :options="businesses"
                :custom-label="businessLabel"
                placeholder="Search business by name..."
                track-by="id"
                :loading="loadingBusinesses"
                :searchable="true"
                :internal-search="false"
                :clear-on-select="false"
                :preserve-search="true"
                :preselect-first="false"
                @search-change="searchBusinesses"
                @select="onBusinessSelected"
                required
              >
                <template slot="noResult">
                  No businesses found. Type to search...
                </template>
                <template slot="noOptions">
                  Type to search for businesses...
                </template>
              </multiselect>
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">Doctor <span class="text-danger">*</span></label>
              <multiselect
                v-model="selectedPractitioner"
                :options="practitioners"
                :custom-label="practitionerLabel"
                placeholder="Select a business first"
                track-by="id"
                :loading="loadingPractitioners"
                :searchable="true"
                :allow-empty="false"
                :clear-on-select="false"
                :preserve-search="true"
                :disabled="!selectedBusiness"
                @search-change="searchPractitioners"
                @select="fetchAvailableSlots"
              >
                <template slot="noResult">
                  No doctors found.
                </template>
                <template slot="noOptions">
                  {{ selectedBusiness ? 'Type to search for doctors...' : 'Select a business first' }}
                </template>
              </multiselect>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Location</label>
              <multiselect
                v-model="selectedLocation"
                :options="locations"
                :custom-label="locationLabel"
                placeholder="Select Location"
                track-by="id"
                :loading="loadingLocations"
                :searchable="true"
                :clear-on-select="false"
                :preserve-search="true"
                :preselect-first="false"
                @search-change="searchLocations"
              >
                <template slot="noResult">
                  No locations found.
                </template>
              </multiselect>
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">Date</label>
              <date-picker
                style="width: 100%"
                v-model="startDate"
                valueType="format"
                type="date"
                @change="fetchAvailableSlots"
                input-class="form-control">
              </date-picker>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12 mb-3">
              <label class="form-label">Service</label>
              <multiselect
                v-model="selectedServices"
                :options="availableServices"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select a service"
                label="name"
                track-by="id"
                :loading="loadingServices"
                @search-change="searchServices"
              >
                <template slot="option" slot-scope="props">
                  <div class="d-flex justify-content-between align-items-center">
                    <span>{{ props.option.name }}</span>
                    <small class="text-muted">{{
                        displayAmount(selectedCurrencyId, props.option.price)
                      }}</small>
                  </div>
                </template>
              </multiselect>
              <small hidden class="text-muted">Search and select a service for this appointment</small>
            </div>
          </div>

          <div class="row" v-if="availableSlots.length > 0">
            <div class="col-md-12 mb-3">
              <label class="form-label">Available Slots <span class="text-danger">*</span></label>
              <div class="row">
                <div class="col-6 col-sm-4 col-md-3 col-lg-2 col-xl-2 mb-2" v-for="slot in availableSlots"
                     :key="slot.id">
                  <button
                    type="button"
                    :class="['btn btn-block', selectedSlot && selectedSlot.id === slot.id ? 'btn-primary' : 'btn-outline-primary']"
                    @click="selectSlot(slot)">
                    {{ formatSlotTime(slot.startDate) }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="row" v-else-if="startDate && selectedPractitioner">
            <div class="col-md-12">
              <div class="alert alert-warning mb-0">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                No available slots for the selected date. Please choose another date.
              </div>
            </div>
          </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="text-right">
            <button
              type="button"
              class="btn btn-secondary mr-2"
              @click="goBack"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveAppointment()"
              :disabled="!selectedBusiness || !selectedPractitioner || !selectedSlot"
            >
              Book Appointment
            </button>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import Multiselect from 'vue-multiselect'
import authHeader from "../../service/auth-header";
import DatePicker from "vue2-datepicker";
import PageLayout from "../layouts/PageLayout.vue";
import FeatureOptInBanner from "../shared/FeatureOptInBanner.vue";
import AppointmentFormSkeleton from "../shared/AppointmentFormSkeleton.vue";
import doctorService from "../../service/doctor.service";
import moment from "moment-timezone";
import visitService from "../../service/appointment.service";
import businessService from "../../service/business.service";
import appointmentSlotService from "../../service/appointment-slot.service";
import {displayAmount} from '../../utils/money';
import {handleError} from '../../utils/notification.utils';
import stockService from "../../service/stock.service";
import {formatSlotTime} from "../../utils/utils";

export default {
  name: "create-appointment",
  components: {
    PageLayout,
    DatePicker,
    Multiselect,
    FeatureOptInBanner,
    AppointmentFormSkeleton
  },
  props: {
    showBanner: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      pageTitle: "Create New Appointment",
      locale: this.$i18n.locale,
      selectedBusiness: null,
      businesses: [],
      loadingBusinesses: false,
      practitioners: [],
      selectedPractitioner: null,
      loadingPractitioners: false,
      selectedLocation: null,
      locations: [],
      allLocations: [],
      loadingLocations: false,
      selectedCurrencyId: undefined,
      selectedPurpose: "Initial Consultation",
      startDate: undefined,
      availableSlots: [],
      selectedSlot: null,
      availableServices: [],
      selectedServices: null,
      loadingServices: false,
      searchTimeout: null,
      isInitialLoading: true,
    }
  },
  computed: {
    totalAmount() {
      return this.selectedServices ? (this.selectedServices.price || 0) : 0;
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    authHeaders() {
      return authHeader()
    },
  },
  methods: {
    formatSlotTime,
    displayAmount,
    businessLabel(business) {
      return business.name || business.businessName || 'Unknown Business';
    },
    practitionerLabel(practitioner) {
      return practitioner.name || practitioner.fullName || 'Unknown Practitioner';
    },
    locationLabel(location) {
      return location.name || location.businessName || 'Unknown Location';
    },
    caseLabel(customerCase) {
      return customerCase.name;
    },
    async searchLocations(query) {
      if (!query || query.length < 2) return;

      // Filter already loaded locations client-side
      const allLocations = this.allLocations || [];
      this.locations = allLocations.filter(location => {
        const name = (location.name || location.businessName || '').toLowerCase();
        return name.includes(query.toLowerCase());
      });
    },
    async searchBusinesses(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(async () => {
        if (query && query.length >= 2) {
          this.loadingBusinesses = true;
          try {
            const params = {
              page: 1,
              size: 50,
              name: query,
              op: 'like'
            };

            const businessType = this.$i18n.locale.toUpperCase();
            const response = await businessService.findAll(params, businessType);
            if (response.data && response.data.code === 'SUCCESS') {
              this.businesses = response.data.data || [];
            }
          } catch (error) {
            console.error('Error loading businesses:', error);
          } finally {
            this.loadingBusinesses = false;
          }
        }
      }, 300);
    },
    async loadInitialBusinesses() {
      this.loadingBusinesses = true;
      try {
        const params = {
          page: 1,
          size: 20
        };

        const businessType = this.$i18n.locale.toUpperCase();
        const response = await businessService.findAll(params, businessType);
        if (response.data && response.data.code === 'SUCCESS') {
          this.businesses = response.data.data || [];
        }
      } catch (error) {
        console.error('Error loading initial businesses:', error);
      } finally {
        this.loadingBusinesses = false;
      }
    },
    async onBusinessSelected(selectedBusiness) {
      if (selectedBusiness && selectedBusiness.id) {
        // Reset practitioners and slots when business changes
        this.practitioners = [];
        this.selectedPractitioner = null;
        this.availableSlots = [];
        this.selectedSlot = null;
        
        // Load doctors for selected business
        await this.loadDoctorsForBusiness(selectedBusiness.id);
      }
    },
    async loadDoctorsForBusiness(businessId) {
      if (!businessId) return;
      
      this.loadingPractitioners = true;
      try {
        const params = {
          page: 1,
          size: 50
        };

        const response = await doctorService.findByBusinessId(businessId, params);
        if (response.data && response.data.code === 'SUCCESS') {
          this.practitioners = response.data.data || [];
          // Auto-select first doctor if available
          if (this.practitioners.length > 0) {
            this.selectedPractitioner = this.practitioners[0];
            this.fetchAvailableSlots();
          }
        }
      } catch (error) {
        console.error('Error loading doctors for business:', error);
        this.fireNotification('warning', 'Warning', 'Could not load doctors for selected business');
      } finally {
        this.loadingPractitioners = false;
      }
    },
    async searchPractitioners(query) {
      if (!this.selectedBusiness || !this.selectedBusiness.id) {
        return;
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(async () => {
        if (query && query.length >= 2) {
          this.loadingPractitioners = true;
          try {
            const params = {
              page: 1,
              size: 50,
              name: query,
              op: 'like'
            };

            const response = await doctorService.findByBusinessId(this.selectedBusiness.id, params);
            if (response.data && response.data.code === 'SUCCESS') {
              this.practitioners = response.data.data || [];
            }
          } catch (error) {
            console.error('Error loading practitioners:', error);
          } finally {
            this.loadingPractitioners = false;
          }
        }
      }, 300);
    },
    getCurrentDate() {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const day = currentDate.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`
    },
    async searchServices(query) {
      // Only search services if a business is selected
      if (!this.selectedBusiness || !this.selectedBusiness.id) {
        return;
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(async () => {
        if (query && query.length >= 2) {
          this.loadingServices = true;
          try {
            const businessId = this.selectedBusiness.id;
            const params = {
              page: 1,
              size: 50,
              name: query,
              op: 'like',
              type: 'SERVICE'
            };

            const response = await stockService.findByBusinessId(businessId, params);
            if (response.data && response.data.code === 'SUCCESS') {
              this.availableServices = response.data.data || [];
            }
          } catch (error) {
            console.error('Error loading services:', error);
          } finally {
            this.loadingServices = false;
          }
        }
      }, 300);
    },
    async loadInitialServices() {
      // Only load services if a business is selected
      if (!this.selectedBusiness || !this.selectedBusiness.id) {
        this.availableServices = [];
        return;
      }

      this.loadingServices = true;
      try {
        const businessId = this.selectedBusiness.id;
        const params = {
          page: 1,
          size: 20,
          type: 'SERVICE',
          statuses: 'ACTIVE'
        };

        const response = await stockService.findByBusinessId(businessId, params);
        if (response.data && response.data.code === 'SUCCESS') {
          this.availableServices = response.data.data || [];
        }
      } catch (error) {
        console.error('Error loading initial services:', error);
      } finally {
        this.loadingServices = false;
      }
    },
    fetchAvailableSlots() {
      if (!this.startDate || !this.selectedPractitioner || !this.selectedBusiness) {
        this.availableSlots = [];
        this.selectedSlot = null;
        return;
      }

      const businessId = this.selectedBusiness.id;
      const resourceId = this.selectedPractitioner.id;

      const startDateTime = moment(this.startDate, "YYYY-MM-DD").startOf('day').utc().toISOString();
      const endDateTime = moment(this.startDate, "YYYY-MM-DD").endOf('day').utc().toISOString();

      appointmentSlotService.getAvailableSlots(businessId, resourceId, startDateTime, endDateTime)
        .then(response => {
          if (response.data.code === 'SUCCESS') {
            this.availableSlots = response.data.data;
            this.selectedSlot = null;
          } else {
            this.availableSlots = [];
            this.fireNotification('warning', "Failed", response.data.description || "Could not fetch available slots");
          }
        })
        .catch(e => {
          console.log(e);
          this.availableSlots = [];
          if (e.response && (e.response.status === 401 || e.response.status === 403 || e.response.status === 409)) {
            return;
          }
          this.fireNotification('error', "Error", "Failed to fetch available slots");
        });
    },
    selectSlot(slot) {
      this.selectedSlot = slot;
    },
    saveAppointment() {
      if (!this.selectedSlot) {
        this.fireNotification('warning', "Required", "Please select a time slot");
        return;
      }

      // Convert to UTC before formatting
      const startDateTimeUtc = moment(this.selectedSlot.startDate).utc();
      const endDateTimeUtc = moment(this.selectedSlot.endDate).utc();

      // Format date and time in UTC
      const startDate = startDateTimeUtc.format("YYYY-MM-DD");
      const startTime = startDateTimeUtc.format("HH:mm[Z]"); // UTC time
      const endTime = endDateTimeUtc.format("HH:mm[Z]");     // UTC time

      let caseId;
      if (this.selectedCustomerCase && this.selectedCustomerCase.id) {
        caseId = this.selectedCustomerCase.id;
      }

      const items = this.selectedServices ? [{
        itemId: this.selectedServices.id,
        itemSku: this.selectedServices.sku,
        quantity: 1,
        name: this.selectedServices.name,
        discount: null,
        tax: null,
        price: this.selectedServices.price,
      }] : [];

      const contextId = localStorage.getItem('contextId') || localStorage.getItem('selectedContextId');
      if (!contextId) {
        this.fireNotification('error', "Error", "Customer context not found. Please log in again.");
        return;
      }

      let appointment = {
        visitorId: contextId,
        businessId: this.selectedBusiness.id,
        destinationId: this.selectedBusiness.id,
        personBeingVisitedId: this.selectedPractitioner.id,
        caseId: caseId,
        purposeOfVisit: this.selectedPurpose,
        startDate: startDate,
        endDate: startDate,
        startTime: startTime,
        endTime: endTime,
        slotId: this.selectedSlot.id,
        items: items,
        customerTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        currency: this.selectedCurrencyId,
        sendConfirmationEmail: true, // Always send notifications in customer portal
      }

      visitService.create(contextId, appointment)
        .then(response => {
          let code = response.data.code;
          if (code === 'SUCCESS') {
            this.success = true;
            this.$router.push(`/appointments/${response.data.data.id}`)
          } else {
            this.fireNotification('warning', "Failed", response.data.narrative)
          }
        })
        .catch(e => {
          handleError(e, "Failed to create appointment. Please try again.");
        })
    },

    async loadInitialLocations() {
      // Mock empty locations for now - will be implemented later
      this.loadingLocations = false;
      this.allLocations = [];
      this.locations = [];
      this.selectedLocation = null;
    },
    fireNotification(icon, title, message) {
      Swal.fire({
        icon: icon,
        title: title,
        text: message,
        footer: '<a href="">Why do I have this issue?</a>'
      })
    },
    goBack() {
      this.$router.push('/appointments');
    },
  },
  watch: {
    'selectedBusiness': {
      handler(newBusiness, oldBusiness) {
        if (newBusiness && newBusiness.id) {
          // Business selected, doctors will be loaded in onBusinessSelected
          // Also reload services for the new business
          if (!oldBusiness || oldBusiness.id !== newBusiness.id) {
            this.loadInitialServices();
          }
        }
      },
      deep: true,
      immediate: true
    },
  },
  async mounted() {
    if (!this.currentUser) {
      this.$router.push('/login')
      return
    }

    // Set date before loading data
    this.startDate = this.getCurrentDate()

    // Load all initial data
    // Set default currency for customer portal
    this.selectedCurrencyId = 'USD';
    
    try {
      await Promise.all([
        this.loadInitialBusinesses(),
        this.loadInitialLocations()
      ]);
      // Note: Services will be loaded after business is selected
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      // Hide skeleton after initial data loads
      this.isInitialLoading = false;
    }
  }
}
</script>

<style scoped>
.card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(.25, .8, .25, 1);
  margin-bottom: 20px;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.btn-block {
  width: 100%;
  margin-bottom: 0.5rem;
}

/* Alert with link styling */
.alert .alert-link {
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.alert .alert-link:hover {
  text-decoration: underline;
}

.alert .alert-link i {
  font-size: 0.9em;
  margin-right: 2px;
}

/* Modal styling */
.modal-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border-bottom: none;
}

.modal-header .close {
  color: white;
  opacity: 0.8;
  text-shadow: none;
}

.modal-header .close:hover {
  opacity: 1;
}

.modal-title {
  display: flex;
  align-items: center;
}

.modal-body .form-group {
  margin-bottom: 1.5rem;
}

.modal-body .alert-info {
  background-color: #e7f3ff;
  border-color: #b3d9ff;
  color: #004085;
}

</style>



// WEBPACK FOOTER //
// src/components/appointments/CreateAppointmentV2.vue